import type { RouteLocationNormalized } from '#vue-router';

// Custom middleware with parts of the original auth-redirect.ts from the supabase module
// Check: https://github.com/nuxt-modules/supabase/blob/main/src/runtime/plugins/auth-redirect.ts

// The user is already logged in but the defined routes have a higher priority than the signup completion
const PUBLIC_ROUTES = ['/termsOfService'];

function abortIfAuthOrPublicRoute(to: RouteLocationNormalized) {
  const config = useRuntimeConfig().public.supabase;
  const { login, callback, exclude } = config.redirectOptions;

  const EXCLUDE_ROUTES = [...(exclude ?? []), ...PUBLIC_ROUTES];

  return [...EXCLUDE_ROUTES, login, callback].some(path => {
    const regex = new RegExp(`^${path.replace(/\*/g, '.*')}$`);
    return regex.test(to.path);
  });
}

export default defineNuxtRouteMiddleware(async to => {
  if (abortIfAuthOrPublicRoute(to)) return;

  const user = useSupabaseUser();
  if (!user.value) {
    return navigateTo({ name: 'login', query: { redirect_back: to.fullPath } });
  }

  const userStore = useUserStore();
  await userStore.fetchUserInformation();

  const onboardingStore = useOnboardingStore();
  await onboardingStore.fetchUserOnboarding();

  const workspace = await useWorkspace();

  const onboardingAddWorkspaceMembersRouteName = 'onboarding.add-members';

  const totalSteps = Math.max(...onboardingStore.onboardingSteps.map(s => s.step));
  const allStepsVisited = [
    ...onboardingStore.onboardingProgress.completed,
    ...onboardingStore.onboardingProgress.skipped,
  ];
  const lastVisitedStep = Math.max(...allStepsVisited, 0);
  const nextStep = onboardingStore.onboardingSteps.find(s => s.step === lastVisitedStep + 1);

  if (workspace && nextStep?.name !== onboardingAddWorkspaceMembersRouteName) {
    onboardingStore.onboardingSteps = onboardingStore.onboardingSteps.filter(
      step => step.name === 'onboarding.account-details'
    );
  }

  if (!nextStep) return;
  if (lastVisitedStep === totalSteps) return;
  if (lastVisitedStep !== totalSteps && to.name === nextStep.name) return;
  if (lastVisitedStep !== totalSteps && to.name !== nextStep.name) return navigateTo({ name: nextStep.name });

  if (lastVisitedStep === totalSteps) return navigateTo({ name: 'dashboard' });
});
